import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  BadgeCheck,
  Copy,
  Mail,
  CreditCard,
  FileCheck2,
  Loader2,
  Plus,
  Ban,
  Wallet,
} from 'lucide-react';
import {
  createLicence,
  fetchCourriersLicence,
  fetchPresetClub,
  encaisserCotisation,
  fetchSaisons,
  annulerReglement,
  updateLicence,
  urlAttestation,
} from '../services/api';
import { useAuth } from '../hooks/useAuth';
import { eurosDepuisCentimes as euros } from '../utils/montants';

type Notice = { type: 'success' | 'error'; message: string } | null;

/** Les moyens qu'un trésorier reçoit vraiment. Le paiement en ligne s'inscrit seul. */
const MODES = [
  { code: 'especes', label: 'Espèces' },
  { code: 'cheque', label: 'Chèque' },
  { code: 'virement', label: 'Virement' },
  { code: 'aide', label: 'Aide ou coupon sport' },
  { code: 'autre', label: 'Autre' },
];

const LIBELLE_STATUT: Record<string, string> = {
  en_attente: 'En attente',
  validee: 'Validée',
  refusee: 'Refusée',
};

/**
 * La licence de la saison, sur la fiche d'un adhérent.
 *
 * Deux chemins mènent au règlement et le club a besoin des deux : la famille
 * qui paie en ligne par le lien, et celle qui tend un chèque au trésorier un
 * soir d'entraînement. Le second reste le plus courant dans un club de
 * village — sa saisie est donc là, sous les yeux, pas cachée derrière un
 * menu.
 */
export default function LicenceAdherent({
  adherent,
  configuration,
  onChangement,
}: {
  adherent: any;
  configuration: any;
  onChangement?: () => void;
}) {
  const { aLeDroit } = useAuth();
  const queryClient = useQueryClient();
  const peutGerer = aLeDroit('gerer_adherents');
  const peutEncaisser = peutGerer || aLeDroit('encaisser_paiements');

  const [notice, setNotice] = useState<Notice>(null);
  const [formOuvert, setFormOuvert] = useState(false);

  const saisonsQuery = useQuery({ queryKey: ['saisons'], queryFn: fetchSaisons });
  const saisons: any[] = saisonsQuery.data?.saisons ?? [];
  const saisonCourante = saisons.find((s) => s.courante) ?? saisons[0] ?? null;

  /*
   * TOUTES ses licences de la saison, et non « sa » licence.
   *
   * Dans une association omnisports, un même adhérent peut être licencié au
   * judo ET à la gymnastique la même année, et régler les deux séparément.
   * « Sa licence de la saison » ne désigne alors plus rien de précis, et
   * n'en montrer qu'une cacherait une cotisation impayée derrière une
   * cotisation soldée.
   *
   * Dans un club mono-discipline — la quasi-totalité d'entre eux — la liste
   * n'en contient qu'une, et l'écran est celui qu'on connaît.
   */
  const licencesSaison = useMemo(() => {
    const liste: any[] = adherent?.licences ?? [];
    if (saisonCourante) {
      return liste.filter((l) => l.saisonId === saisonCourante.id);
    }
    return liste.slice(0, 1);
  }, [adherent, saisonCourante]);

  const idsSaison = new Set(licencesSaison.map((l: any) => l.id));
  const anciennes: any[] = (adherent?.licences ?? []).filter((l: any) => !idsSaison.has(l.id));

  /*
   * Les disciplines dans lesquelles il n'est PAS encore licencié.
   *
   * C'est ce qui décide du bouton : « Inscrire » quand il n'a rien, « Inscrire
   * dans une autre discipline » quand il en a déjà une et qu'il en reste, et
   * rien du tout quand toutes sont prises — un bouton qui ne peut mener qu'à
   * un refus vaut moins que pas de bouton.
   */
  const presetQuery = useQuery({ queryKey: ['preset-club'], queryFn: fetchPresetClub });
  const disciplines: any[] = presetQuery.data?.presets ?? [];
  const dejaPrises = new Set(licencesSaison.map((l: any) => l.sportPresetId).filter(Boolean));
  const restantes = disciplines.filter((d) => !dejaPrises.has(d.id));

  const annoncer = (type: 'success' | 'error', message: string) => {
    setNotice({ type, message });
    if (type === 'success') setTimeout(() => setNotice(null), 3000);
  };

  const rafraichir = () => {
    queryClient.invalidateQueries({ queryKey: ['adherent', adherent?.id] });
    queryClient.invalidateQueries({ queryKey: ['adherents'] });
    queryClient.invalidateQueries({ queryKey: ['licences'] });
    onChangement?.();
  };

  const inscrire = useMutation({
    mutationFn: (donnees: Record<string, unknown>) => createLicence(adherent.id, donnees),
    onSuccess: (d: any) => {
      annoncer('success', d.message);
      setFormOuvert(false);
      rafraichir();
    },
    onError: (e: Error) => annoncer('error', e.message),
  });


  if (saisonsQuery.isPending) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 p-5 flex justify-center">
        <Loader2 className="w-5 h-5 animate-spin text-primary-600" />
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-5">
      <h3 className="text-sm font-semibold text-gray-900 mb-1 flex items-center gap-2">
        <BadgeCheck className="w-4 h-4 text-primary-600" />
        Licence et cotisation
      </h3>
      <p className="text-xs text-gray-500 mb-4">
        {saisonCourante
          ? `Saison ${saisonCourante.libelle}`
          : 'Aucune saison ouverte : créez-en une dans Administration → Adhérents.'}
      </p>

      {notice && (
        <div
          className={`mb-4 rounded-lg px-3 py-2 text-sm ${
            notice.type === 'success'
              ? 'bg-green-50 text-green-800 border border-green-200'
              : 'bg-red-50 text-red-800 border border-red-200'
          }`}
        >
          {notice.message}
        </div>
      )}

      {/* ---------------------- Les licences de la saison ---------------------- */}
      {licencesSaison.map((l: any) => (
        <BlocLicence
          key={l.id}
          licence={l}
          configuration={configuration}
          peutGerer={peutGerer}
          peutEncaisser={peutEncaisser}
          /*
            Le nom de la discipline en tête, et seulement quand il y en a
            plusieurs. L'écrire dans un club mono-discipline ajouterait un
            titre qui ne distingue rien de rien.
          */
          titre={licencesSaison.length > 1 ? l.discipline : null}
          onNotice={annoncer}
          onChangement={rafraichir}
        />
      ))}

      {/* --------------------------- Une inscription --------------------------- */}
      {saisonCourante !== null && (restantes.length > 0 || licencesSaison.length === 0) && (
        <>
          {!formOuvert ? (
            <div className="flex items-center justify-between gap-4 rounded-lg bg-gray-50 border border-gray-100 p-4 mb-4">
              <p className="text-sm text-gray-600">
                {licencesSaison.length === 0
                  ? `${adherent.prenom} n'est pas encore inscrit pour cette saison.`
                  : `${adherent.prenom} peut aussi être licencié en ${restantes
                      .map((d: any) => d.nom)
                      .join(', ')}.`}
              </p>
              {peutGerer && (
                <button
                  onClick={() => setFormOuvert(true)}
                  className="shrink-0 inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-primary-600 text-white text-sm font-medium hover:bg-primary-700"
                >
                  <Plus className="w-4 h-4" />
                  {licencesSaison.length === 0 ? 'Inscrire' : 'Autre discipline'}
                </button>
              )}
            </div>
          ) : (
            <FormulaireLicence
              configuration={configuration}
              saison={saisonCourante}
              /*
                Les disciplines PROPOSÉES sont celles qui restent, pas toutes.
                Offrir celle où il est déjà licencié mènerait à un refus du
                serveur — juste, mais découvert après avoir rempli le tarif et
                l'échéance.
              */
              disciplines={restantes}
              onAnnuler={() => setFormOuvert(false)}
              onValider={(donnees) => inscrire.mutate({ ...donnees, saison: saisonCourante.id })}
              enCours={inscrire.isPending}
            />
          )}
        </>
      )}

      {/* ------------------------- Les saisons passées ------------------------- */}
      {anciennes.length > 0 && (
        <div className="border-t border-gray-100 mt-4 pt-3">
          <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">
            Saisons précédentes
          </h4>
          <ul className="space-y-1 text-sm">
            {anciennes.map((l: any) => (
              <li key={l.id} className="flex items-center justify-between gap-3 text-gray-600">
                <span>
                  {l.saison}
                  {l.categorie ? ` · ${l.categorie}` : ''}
                  {l.numero ? ` · n° ${l.numero}` : ''}
                </span>
                <span className={l.reste > 0 ? 'text-amber-700' : 'text-gray-400'}>
                  {l.reste > 0 ? `${euros(l.reste)} non réglés` : euros(l.regle)}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

/**
 * Une licence, et tout ce qui s'y rattache.
 *
 * POURQUOI CE BLOC EST DEVENU UN COMPOSANT
 * ---------------------------------------------------------------------------
 * Il vivait dans le corps de LicenceAdherent, avec ses mutations et son état
 * de saisie tenus par le parent. C'était juste tant qu'il n'y avait qu'une
 * licence par saison : un seul montant en cours de frappe, un seul « lien
 * copié », une seule cible pour « modifier ».
 *
 * Dans une association omnisports il y en a plusieurs, et un état partagé
 * ferait exactement ce qu'on redoute : taper 40 € dans le bloc « Judo »
 * remplirait aussi le champ du bloc « Gymnastique », et l'encaissement
 * partirait sur celle dont on ne parlait pas.
 *
 * Chaque bloc porte donc son état et ses mutations. C'est la seule façon
 * d'avoir deux formulaires côte à côte sans qu'ils se marchent dessus.
 */
function BlocLicence({
  licence,
  configuration,
  peutGerer,
  peutEncaisser,
  titre,
  onNotice,
  onChangement,
}: {
  licence: any;
  configuration: any;
  peutGerer: boolean;
  peutEncaisser: boolean;
  /** Le nom de la discipline, quand le club en gère plusieurs. */
  titre: string | null;
  onNotice: (type: 'success' | 'error', message: string) => void;
  onChangement: () => void;
}) {
  const [versement, setVersement] = useState({ montant: '', mode: 'especes', reference: '', recuLe: '' });
  const [copie, setCopie] = useState(false);

  const modifier = useMutation({
    mutationFn: (donnees: Record<string, unknown>) => updateLicence(licence.id, donnees),
    onSuccess: (d: any) => {
      onNotice('success', d.message);
      onChangement();
    },
    onError: (e: Error) => onNotice('error', e.message),
  });

  const encaisser = useMutation({
    mutationFn: (donnees: Record<string, unknown>) => encaisserCotisation(licence.id, donnees),
    onSuccess: (d: any) => {
      onNotice('success', d.message);
      setVersement({ montant: '', mode: 'especes', reference: '', recuLe: '' });
      onChangement();
    },
    onError: (e: Error) => onNotice('error', e.message),
  });

  const retirerVersement = useMutation({
    mutationFn: ({ id, motif }: { id: number; motif: string }) => annulerReglement(id, motif),
    onSuccess: (d: any) => {
      onNotice('success', d.message);
      onChangement();
    },
    onError: (e: Error) => onNotice('error', e.message),
  });

  const copierLien = () => {
    if (!licence?.lienPaiement) return;
    const lien = `${window.location.origin}${licence.lienPaiement}`;
    navigator.clipboard?.writeText(lien).then(() => {
      setCopie(true);
      setTimeout(() => setCopie(false), 2500);
    });
  };

  return (
    <div className={titre === null ? 'space-y-4' : 'space-y-4 rounded-xl border border-gray-200 p-4 mb-4'}>
      {titre !== null && (
        <h4 className="flex items-center gap-2 text-sm font-semibold text-gray-900">
          <BadgeCheck className="w-4 h-4 text-primary-600" />
          {titre}
        </h4>
      )}

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <Chiffre libelle="Cotisation" valeur={euros(licence.montant)} />
            <Chiffre libelle="Réglé" valeur={euros(licence.regle)} ton="vert" />
            <Chiffre
              libelle="Reste"
              valeur={euros(licence.reste)}
              ton={licence.reste > 0 ? (licence.enRetard ? 'rouge' : 'ambre') : 'neutre'}
            />
            <Chiffre libelle="Statut" valeur={LIBELLE_STATUT[licence.statut] ?? licence.statut} />
          </div>

          {licence.surclasse && (
            <p className="text-sm rounded-lg bg-indigo-50 border border-indigo-200 text-indigo-900 px-3 py-2">
              <strong>
                {licence.categorieAge && licence.categorie > licence.categorieAge
                  ? 'Surclassé'
                  : 'Catégorie ajustée'}
              </strong>{' '}
              en {licence.categorie} alors que son âge le place en {licence.categorieAge}.
              {licence.surclassementMotif && <> {licence.surclassementMotif}</>}
              {licence.surclassePar && (
                <span className="text-indigo-700"> — décidé par {licence.surclassePar}.</span>
              )}
            </p>
          )}

          {licence.enRetard && (
            <p className="text-sm rounded-lg bg-red-50 border border-red-200 text-red-800 px-3 py-2">
              Cotisation en retard : l'échéance du {licence.echeance} est passée.
            </p>
          )}

          <dl className="grid grid-cols-1 sm:grid-cols-3 gap-x-6 gap-y-2 text-sm border-t border-gray-100 pt-3">
            <Ligne libelle="Numéro de licence" valeur={licence.numero} />
            <Ligne libelle="Type" valeur={licence.type} />
            <Ligne
              libelle="Catégorie"
              valeur={
                licence.surclasse
                  ? `${licence.categorie} (son âge : ${licence.categorieAge})`
                  : licence.categorie
              }
            />
            <Ligne libelle="Discipline" valeur={licence.discipline} />
            <Ligne libelle="Délivrée le" valeur={licence.delivreeLe} />
            <Ligne libelle="Échéance" valeur={licence.echeance} />
          </dl>

          {/* Le numéro se recopie à la main : aucune fédération ne le rend. */}
          {peutGerer && (
            <div className="flex flex-wrap items-end gap-3 border-t border-gray-100 pt-3">
              <label className="text-xs text-gray-500">
                Numéro de licence
                <input
                  defaultValue={licence.numero ?? ''}
                  onBlur={(e) => {
                    if (e.target.value !== (licence.numero ?? '')) {
                      modifier.mutate({ numero: e.target.value });
                    }
                  }}
                  placeholder="Recopié de la fédération"
                  className="block mt-1 px-3 py-1.5 rounded-lg border border-gray-300 text-sm text-gray-800 w-56"
                />
              </label>
              <select
                value={licence.statut}
                onChange={(e) => modifier.mutate({ statut: e.target.value })}
                className="px-3 py-1.5 rounded-lg border border-gray-300 text-sm"
              >
                {Object.entries(LIBELLE_STATUT).map(([code, label]) => (
                  <option key={code} value={code}>
                    {label}
                  </option>
                ))}
              </select>
              <label className="text-xs text-gray-500">
                Échéance de paiement
                <input
                  type="date"
                  defaultValue={licence.echeance ?? ''}
                  onBlur={(e) => {
                    if (e.target.value !== (licence.echeance ?? '')) {
                      modifier.mutate({ echeance: e.target.value });
                    }
                  }}
                  className="block mt-1 px-3 py-1.5 rounded-lg border border-gray-300 text-sm text-gray-800"
                />
              </label>
            </div>
          )}

          {/* Le surclassement : la décision appartient à l'entraîneur, mais
              elle se motive — une catégorie qui s'écarte de l'âge sans
              explication ressemble à une faute de frappe, et le sera un jour. */}
          {peutGerer && (
            <Surclassement
              licence={licence}
              configuration={configuration}
              onValider={(donnees) => modifier.mutate(donnees)}
              enCours={modifier.isPending}
            />
          )}

          {/* --------------------------- Versements --------------------------- */}
          <div className="border-t border-gray-100 pt-4">
            <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">
              Versements
            </h4>
            {licence.reglements?.length ? (
              <ul className="divide-y divide-gray-100 mb-3">
                {licence.reglements.map((r: any) => (
                  <li key={r.id} className="flex items-center gap-3 py-2 text-sm">
                    {r.mode === 'en_ligne' ? (
                      <CreditCard className="w-4 h-4 text-gray-400 shrink-0" />
                    ) : (
                      <Wallet className="w-4 h-4 text-gray-400 shrink-0" />
                    )}
                    <span className="flex-1 min-w-0">
                      {/*
                        Un versement annulé reste affiché, barré.

                        L'effacer le rendrait invisible, ce qui reviendrait à
                        l'avoir supprimé du point de vue de qui regarde l'écran.
                        Tout l'intérêt de ne pas le détruire est qu'on le VOIE,
                        avec son motif et le nom de qui l'a annulé.
                      */}
                      <span
                        className={
                          r.annule === true
                            ? 'block text-gray-400 line-through'
                            : 'block text-gray-800'
                        }
                      >
                        {euros(r.montant)} — {r.modeLibelle}
                      </span>
                      <span className="block text-xs text-gray-500 truncate">
                        {[r.recuLe, r.reference, r.encaissePar ? `reçu par ${r.encaissePar}` : null]
                          .filter(Boolean)
                          .join(' · ')}
                      </span>
                      {r.annule === true && (
                        <span className="block text-xs text-red-700">
                          Annulé{r.annulePar ? ` par ${r.annulePar}` : ''}
                          {r.motifAnnulation ? ` — ${r.motifAnnulation}` : ''}
                        </span>
                      )}
                    </span>
                    {peutEncaisser && r.annule !== true && (
                      <button
                        onClick={() => {
                          /*
                            Le motif est demandé, pas facultatif.

                            « Erreur de montant » et « chèque impayé » ne se
                            rattrapent pas de la même façon : le premier se
                            ressaisit, le second se relance auprès de l'adhérent.
                            Dans six mois, cette phrase sera la seule chose qui
                            permettra de trancher.
                          */
                          const motif = window.prompt(
                            `Annuler ce versement de ${euros(r.montant)} ?\n\n` +
                              'Il restera visible, barré, avec votre motif. Dites pourquoi :',
                          );
                          if (motif !== null && motif.trim() !== '') {
                            retirerVersement.mutate({ id: r.id, motif: motif.trim() });
                          }
                        }}
                        title="Annuler ce versement"
                        className="p-1.5 rounded text-red-600 hover:bg-red-50"
                      >
                        <Ban className="w-4 h-4" />
                      </button>
                    )}
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-sm text-gray-500 mb-3">Rien n'a encore été encaissé.</p>
            )}

            {peutEncaisser && licence.reste > 0 && (
              <div className="flex flex-wrap items-center gap-2 rounded-lg bg-gray-50 border border-gray-100 p-3">
                <input
                  value={versement.montant}
                  onChange={(e) => setVersement({ ...versement, montant: e.target.value })}
                  placeholder={(licence.reste / 100).toFixed(2)}
                  inputMode="decimal"
                  className="w-24 px-3 py-1.5 rounded-lg border border-gray-300 text-sm"
                />
                <span className="text-sm text-gray-500">€</span>
                <select
                  value={versement.mode}
                  onChange={(e) => setVersement({ ...versement, mode: e.target.value })}
                  className="px-3 py-1.5 rounded-lg border border-gray-300 text-sm"
                >
                  {MODES.map((m) => (
                    <option key={m.code} value={m.code}>
                      {m.label}
                    </option>
                  ))}
                </select>
                <input
                  value={versement.reference}
                  onChange={(e) => setVersement({ ...versement, reference: e.target.value })}
                  placeholder="N° de chèque, de coupon…"
                  className="flex-1 min-w-[10rem] px-3 py-1.5 rounded-lg border border-gray-300 text-sm"
                />
                <input
                  type="date"
                  value={versement.recuLe}
                  onChange={(e) => setVersement({ ...versement, recuLe: e.target.value })}
                  className="px-3 py-1.5 rounded-lg border border-gray-300 text-sm"
                />
                <button
                  onClick={() =>
                    encaisser.mutate({
                      montant: versement.montant || licence.reste / 100,
                      mode: versement.mode,
                      reference: versement.reference,
                      recuLe: versement.recuLe,
                    })
                  }
                  disabled={encaisser.isPending}
                  className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-primary-600 text-white text-sm font-medium hover:bg-primary-700 disabled:opacity-60"
                >
                  {encaisser.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                  Encaisser
                </button>
              </div>
            )}
          </div>

          {/* ------------------- Le lien de paiement, l'attestation, les courriers ------------------- */}
          <div className="flex flex-wrap items-center gap-3 border-t border-gray-100 pt-4">
            <BoutonCourriers licence={licence} />
            {licence.lienPaiement && (
              <button
                onClick={copierLien}
                className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg border border-gray-300 text-sm text-gray-700 hover:bg-gray-50"
                title="Le lien que la famille ouvre pour régler en ligne, sans compte"
              >
                <Copy className="w-4 h-4" />
                {copie ? 'Lien copié' : 'Copier le lien de paiement'}
              </button>
            )}
            {licence.regle > 0 && (
              <a
                href={urlAttestation(licence.id)}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg border border-primary-200 text-primary-700 text-sm font-medium hover:bg-primary-50"
              >
                <FileCheck2 className="w-4 h-4" />
                Attestation de paiement
              </a>
            )}
          </div>
    </div>
  );
}

function Chiffre({ libelle, valeur, ton = 'neutre' }: { libelle: string; valeur: string; ton?: string }) {
  const couleurs: Record<string, string> = {
    neutre: 'text-gray-900',
    vert: 'text-green-700',
    ambre: 'text-amber-700',
    rouge: 'text-red-700',
  };

  return (
    <div className="rounded-lg bg-gray-50 border border-gray-100 px-3 py-2">
      <p className="text-xs uppercase tracking-wide text-gray-400">{libelle}</p>
      <p className={`text-base font-semibold ${couleurs[ton]}`}>{valeur}</p>
    </div>
  );
}

function Ligne({ libelle, valeur }: { libelle: string; valeur?: string | null }) {
  return (
    <div>
      <dt className="text-xs uppercase tracking-wide text-gray-400">{libelle}</dt>
      <dd className="text-gray-800">{valeur || <span className="text-gray-300">—</span>}</dd>
    </div>
  );
}

/** Le formulaire d'inscription : le tarif se propose, il ne s'impose pas. */
function FormulaireLicence({
  configuration,
  saison,
  disciplines,
  onAnnuler,
  onValider,
  enCours,
}: {
  configuration: any;
  saison: any;
  /**
   * Les disciplines dans lesquelles il peut encore être licencié.
   *
   * Vide ou d'un seul élément dans un club mono-discipline : il n'y a alors
   * rien à choisir, et le serveur prend la seule en service. Le sélecteur ne
   * s'affiche qu'à partir de deux — un menu à une entrée demande un geste pour
   * une décision qui n'existe pas.
   */
  disciplines: any[];
  onAnnuler: () => void;
  onValider: (donnees: Record<string, unknown>) => void;
  enCours: boolean;
}) {
  const [form, setForm] = useState({
    type: '',
    montant: '',
    echeance: '',
    statut: 'en_attente',
    /*
      La pratique : valide ou handisport.

      Vide par défaut, et non « valide ». Un champ prérempli est un champ
      qu'on ne lit pas : sur un club qui a ouvert sa section, la moitié des
      licences handisport seraient enregistrées comme valides parce que
      personne n'a touché au menu.

      Vide, le serveur enregistre null — qui se lit comme la pratique
      ordinaire. La différence est qu'on saura que la question n'a pas été
      posée, au lieu de croire qu'on y a répondu.
    */
    pratique: '',
    /*
      Préremplie quand il n'y a qu'un choix possible.

      Le serveur saurait la déduire seul dans ce cas, mais l'envoyer quand même
      évite de dépendre de deux chemins différents selon le nombre de
      disciplines — et c'est le genre de bifurcation qui se casse le jour où
      un club en ouvre une seconde.
    */
    sportPreset: disciplines.length > 0 ? String(disciplines[0].id) : '',
  });

  const tarifs: any[] = configuration?.cotisations ?? [];

  return (
    <div className="rounded-lg border border-gray-200 p-4 space-y-3">
      <p className="text-sm text-gray-600">
        Inscription pour la saison <strong>{saison.libelle}</strong>.
      </p>

      {disciplines.length > 1 && (
        <label className="block text-xs text-gray-500">
          Discipline
          <select
            value={form.sportPreset}
            onChange={(e) => setForm({ ...form, sportPreset: e.target.value })}
            className="block w-full mt-1 px-3 py-2 rounded-lg border border-gray-300 text-sm text-gray-800"
          >
            {disciplines.map((d: any) => (
              <option key={d.id} value={String(d.id)}>
                {d.nom} — {d.federation}
              </option>
            ))}
          </select>
          <span className="block mt-1 text-xs text-gray-500">
            Chaque discipline a ses propres catégories d'âge, son tarif et sa cotisation à régler.
          </span>
        </label>
      )}

      {/*
        LA PRATIQUE NE PARAÎT QUE SI LE CLUB A UNE SECTION HANDISPORT.

        C'est un réglage, fermé par défaut. Poser la question à tout le monde
        ferait répondre « valide » cinq cents fois pour rien — et une case
        qu'on coche sans y penser finit par être cochée de travers.

        Elle est à part du type de licence, et non dans le même menu : le type
        dit ce que la licence AUTORISE — compétition, loisir, arbitre — et la
        pratique dit COMMENT on joue. Les deux se croisent, et il existe des
        licenciés handisport en compétition.
      */}
      {configuration?.handisport?.actif ? (
        <label className="block text-xs text-gray-500 dark:text-gray-400">
          Pratique
          <select
            value={form.pratique}
            onChange={(e) => setForm({ ...form, pratique: e.target.value })}
            className="mt-1 w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 text-sm"
          >
            <option value="">Non précisée</option>
            <option value="valide">Valide</option>
            <option value="handisport">Handisport</option>
          </select>
          <span className="mt-1 block">
            L'affiliation n'est pas la même : une section handisport relève de la FFH ou de la
            FFSA, en plus de la fédération de la discipline.
          </span>
        </label>
      ) : null}

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <select
          value={form.type}
          onChange={(e) => setForm({ ...form, type: e.target.value })}
          className="px-3 py-2 rounded-lg border border-gray-300 text-sm"
        >
          <option value="">Type de licence…</option>
          {(configuration?.typesLicence ?? []).map((t: string) => (
            <option key={t} value={t}>
              {t}
            </option>
          ))}
        </select>
        <div className="flex items-center gap-2">
          <input
            value={form.montant}
            onChange={(e) => setForm({ ...form, montant: e.target.value })}
            placeholder="Montant de la cotisation"
            inputMode="decimal"
            className="flex-1 px-3 py-2 rounded-lg border border-gray-300 text-sm"
          />
          <span className="text-sm text-gray-500">€</span>
        </div>
        <label className="text-xs text-gray-500 sm:col-span-2">
          Échéance de paiement
          <input
            type="date"
            value={form.echeance}
            onChange={(e) => setForm({ ...form, echeance: e.target.value })}
            className="block w-full mt-1 px-3 py-2 rounded-lg border border-gray-300 text-sm text-gray-800"
          />
        </label>
      </div>

      {tarifs.length > 0 && (
        <div className="flex flex-wrap gap-2">
          <span className="text-xs text-gray-500 self-center">Tarifs du club :</span>
          {tarifs.map((t: any, i: number) => (
            <button
              key={i}
              onClick={() => setForm({ ...form, montant: String(t.montant) })}
              className="rounded-full bg-gray-100 px-3 py-1 text-xs text-gray-700 hover:bg-gray-200"
            >
              {t.libelle} — {t.montant} €
            </button>
          ))}
        </div>
      )}

      <div className="flex justify-end gap-3">
        <button onClick={onAnnuler} className="px-3 py-1.5 text-sm text-gray-600 hover:text-gray-800">
          Annuler
        </button>
        <button
          onClick={() => onValider(form)}
          disabled={enCours}
          className="inline-flex items-center gap-2 px-4 py-1.5 rounded-lg bg-primary-600 text-white text-sm font-medium hover:bg-primary-700 disabled:opacity-60"
        >
          {enCours && <Loader2 className="w-4 h-4 animate-spin" />}
          Inscrire
        </button>
      </div>
    </div>
  );
}

/**
 * Surclasser ou déclasser un joueur.
 *
 * Un enfant en avance joue avec les plus grands, un autre revient de blessure
 * et redescend d'une catégorie. Le geste est rare : il reste replié tant
 * qu'on ne le demande pas, et réclame un motif avant d'aboutir.
 */
function Surclassement({
  licence,
  configuration,
  onValider,
  enCours,
}: {
  licence: any;
  configuration: any;
  onValider: (donnees: Record<string, unknown>) => void;
  enCours: boolean;
}) {
  const [ouvert, setOuvert] = useState(false);
  const [categorie, setCategorie] = useState(licence.categorie ?? '');
  const [motif, setMotif] = useState(licence.surclassementMotif ?? '');

  const categories: string[] = (configuration?.categories ?? []).map((c: any) => c.libelle);
  const ecart = categorie !== '' && categorie !== licence.categorieAge;

  if (!ouvert) {
    return (
      <button
        onClick={() => setOuvert(true)}
        className="text-sm text-primary-700 hover:text-primary-800 border-t border-gray-100 pt-3 w-full text-left"
      >
        {licence.surclasse ? 'Revoir le surclassement' : 'Surclasser ou déclasser ce joueur'}
      </button>
    );
  }

  return (
    <div className="border-t border-gray-100 pt-3 space-y-2">
      <p className="text-xs text-gray-500">
        Son âge le place en <strong>{licence.categorieAge ?? 'aucune catégorie'}</strong>.
      </p>
      <div className="flex flex-wrap items-center gap-2">
        <select
          value={categorie}
          onChange={(e) => setCategorie(e.target.value)}
          className="px-3 py-1.5 rounded-lg border border-gray-300 text-sm"
        >
          {categories.map((c) => (
            <option key={c} value={c}>
              {c}
            </option>
          ))}
        </select>
        <input
          value={motif}
          onChange={(e) => setMotif(e.target.value)}
          placeholder={ecart ? 'Pourquoi ? *' : 'Motif (facultatif)'}
          className={`flex-1 min-w-[12rem] px-3 py-1.5 rounded-lg border text-sm ${
            ecart && motif.trim() === '' ? 'border-amber-300 bg-amber-50' : 'border-gray-300'
          }`}
        />
        <button
          onClick={() => {
            onValider({ categorie, surclassementMotif: motif });
            setOuvert(false);
          }}
          disabled={enCours || (ecart && motif.trim() === '')}
          className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-primary-600 text-white text-sm font-medium hover:bg-primary-700 disabled:opacity-60"
        >
          {enCours && <Loader2 className="w-4 h-4 animate-spin" />}
          Enregistrer
        </button>
        <button onClick={() => setOuvert(false)} className="text-sm text-gray-500 hover:text-gray-700">
          Annuler
        </button>
      </div>
    </div>
  );
}

/**
 * Les courriers types de la discipline, remplis pour cette licence.
 *
 * CE QUE CE BOUTON RÉPARE
 * ---------------------------------------------------------------------------
 * Les courriers types étaient stockés, relus, validés — et jamais remplis.
 * L'écran de réglages promettait que « les repères entre accolades sont
 * remplis à l'envoi », et rien ne les remplissait : le club recopiait à la
 * main le prénom, la catégorie et le montant dans son logiciel de courriel.
 *
 * CEUX DE SA DISCIPLINE, ET D'ELLE SEULE
 * ---------------------------------------------------------------------------
 * Un club omnisports a un jeu de courriers par section. Proposer ceux du judo
 * pour une licence de gymnastique ferait partir un accueil qui parle d'ippon à
 * une famille qui vient de s'inscrire aux agrès.
 *
 * ON COPIE, ON N'ENVOIE PAS
 * ---------------------------------------------------------------------------
 * Le courrier part par le canal que le club emploie déjà — sa messagerie, ou
 * l'écran de diffusion. Ajouter ici un envoi qui court-circuiterait les deux
 * donnerait un troisième endroit d'où partent des messages, et personne ne
 * saurait plus lequel a envoyé quoi.
 */
function BoutonCourriers({ licence }: { licence: any }) {
  const [ouvert, setOuvert] = useState(false);
  const [copie, setCopie] = useState<string | null>(null);

  const courriers = useQuery({
    queryKey: ['courriers-licence', licence.id],
    queryFn: () => fetchCourriersLicence(licence.id),
    enabled: ouvert,
  });

  const liste = courriers.data?.courriers ?? [];

  return (
    <>
      <button
        onClick={() => setOuvert((o) => !o)}
        className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg border border-gray-300 text-sm text-gray-700 hover:bg-gray-50 min-h-[44px]"
        title="Les courriers types de la discipline, avec le nom, la catégorie et les montants déjà remplis"
      >
        <Mail className="w-4 h-4" />
        Courriers types
      </button>

      {ouvert && (
        <div className="w-full mt-2 rounded-lg border border-gray-200 p-3">
          {courriers.isPending ? (
            <div className="flex justify-center py-3">
              <Loader2 className="w-4 h-4 animate-spin text-primary-600" />
            </div>
          ) : liste.length === 0 ? (
            <p className="text-sm text-gray-500">
              Aucun courrier type pour cette discipline. Vous en écrivez dans Réglages du registre →
              Adapter à votre club → Courriers types.
            </p>
          ) : (
            <ul className="space-y-3">
              {liste.map((c) => (
                <li key={c.code} className="rounded-lg bg-gray-50 border border-gray-100 p-3">
                  <p className="text-sm font-semibold text-gray-900">{c.libelle}</p>
                  <p className="mt-0.5 text-sm text-gray-700">{c.sujet}</p>
                  <pre className="mt-2 max-h-48 overflow-y-auto whitespace-pre-wrap font-sans text-sm text-gray-600">
                    {c.corps}
                  </pre>
                  <button
                    onClick={() => {
                      navigator.clipboard
                        ?.writeText(`${c.sujet}\n\n${c.corps}`)
                        .then(() => {
                          setCopie(c.code);
                          setTimeout(() => setCopie(null), 2500);
                        });
                    }}
                    className="mt-2 inline-flex items-center gap-1.5 text-sm font-medium text-primary-700 hover:underline"
                  >
                    <Copy className="w-3.5 h-3.5" />
                    {copie === c.code ? 'Copié' : 'Copier le courrier'}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </>
  );
}
